// Zero-dependency, in-browser image normalizer used before upload.
//
// Why this exists: covers (and other images) used to be uploaded raw — a 5 MB phone
// photo was stored and served as 5 MB. This shrinks any image to a sane "master" size
// (≤ maxEdge on the longest side) and re-encodes it to WebP, so the backend never has
// to deal with multi-MB originals. The backend still owns generating the thumb/detail
// variants (see cover-image-spec.md); this is just an upload-side safety net.
//
// Only raster photo/illustration formats are touched. SVG (vector) and GIF (possibly
// animated) are passed through untouched. If anything goes wrong, we fall back to the
// original file so an upload is never blocked by compression.

const COMPRESSIBLE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const DEFAULTS = {
  maxEdge: 1024, // longest side in px; book-cover master grade. Never upscales.
  quality: 0.8, // WebP quality (0–1)
  mimeType: "image/webp",
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

/**
 * Compress/resize a single image File before upload.
 * - Skips non-image, SVG, and GIF files (returns them unchanged).
 * - Skips if the result would not be smaller than the original.
 * - Never upscales (only shrinks when longest edge > maxEdge).
 * - Falls back to the original file on any failure.
 *
 * @param {File} file
 * @param {{maxEdge?:number, quality?:number, mimeType?:string}} [options]
 * @returns {Promise<File>}
 */
export const compressImageFile = async (file, options = {}) => {
  try {
    if (!file || !file.type || !COMPRESSIBLE_TYPES.includes(file.type)) {
      return file; // not a compressible raster image (svg/gif/audio/video/etc.)
    }

    const { maxEdge, quality, mimeType } = { ...DEFAULTS, ...options };

    const img = await loadImage(file);
    const { naturalWidth: w, naturalHeight: h } = img;
    if (!w || !h) return file;

    // Only shrink — keep aspect ratio, never enlarge.
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, quality)
    );

    // Browser couldn't encode WebP, or the result isn't actually smaller — keep original.
    if (!blob || blob.size >= file.size) return file;

    const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
    const ext = mimeType === "image/webp" ? "webp" : "jpg";
    return new File([blob], `${baseName}.${ext}`, {
      type: mimeType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("compressImageFile failed, using original:", error);
    return file;
  }
};
